import { manhattanPathAndLabelPosition } from './calculateLabelPosition.tsx';
import { getLabelPlacement, type OverlapPlacement } from './getLabelPlacement.tsx';
import {
	findPort,
	isPortMovingForReroutingTransition,
	isEveryPropertyDefined,
} from './isEveryPropertyDefined.tsx';
import type { ReroutingTransition } from './ReroutingTransitionValid.types.tsx';
import {
	type PortLayout,
	PortIndex,
	type DiagramSharedTransition,
	type PortInfo,
	type SharedTransitionLayout,
} from './toPortInfo.tsx';

export const START_PATH_OFFSET_LENGTH = 80;

// TODO test all these utils in subsequent PR for HIX-4580

export const getConvergingPortLayout = (port: PortLayout): PortLayout => {
	const newPosition = { ...port.position };
	switch (port.portIndex) {
		case PortIndex.TopLeft:
		case PortIndex.Top:
		case PortIndex.TopRight:
			newPosition.y = port.position.y - START_PATH_OFFSET_LENGTH;
			break;
		case PortIndex.Right:
			newPosition.x = port.position.x + START_PATH_OFFSET_LENGTH;
			break;
		case PortIndex.BottomRight:
		case PortIndex.Bottom:
		case PortIndex.BottomLeft:
			newPosition.y = port.position.y + START_PATH_OFFSET_LENGTH;
			break;
		// case PortIndex.Left
		default:
			newPosition.x = port.position.x - START_PATH_OFFSET_LENGTH;
			break;
	}

	return {
		position: newPosition,
		portIndex: port.portIndex,
	};
};

type SharedTransitionConnections = SharedTransitionLayout['connections'];

export const getSharedTransitionLayout = (
	transition: DiagramSharedTransition,
	ports: Record<string, PortInfo>,
	transitionLabelPlacements: OverlapPlacement[],
	reroutingTransition: ReroutingTransition,
): SharedTransitionLayout => {
	const emptyLayout: SharedTransitionLayout = {
		connections: undefined,
	};

	if (
		transition.links.length === 0 ||
		!isEveryPropertyDefined(transition.links, 'sourceIndex', 'targetIndex')
	) {
		return emptyLayout;
	}

	const connections: SharedTransitionConnections = [];

	let didAlreadyMoveTargetPort = false;

	let linkIndex = 0;
	for (const link of transition.links) {
		const source = link.source;
		const sourceIndex = link.sourceIndex;
		const targetIndex = link.targetIndex;

		let sourcePort = findPort(ports, source, sourceIndex);

		const isSourcePortMoving = isPortMovingForReroutingTransition(
			sourcePort,
			'source',
			transition.id,
			reroutingTransition,
		);

		if (!sourcePort) {
			return emptyLayout;
		}

		if (isSourcePortMoving) {
			sourcePort = { ...sourcePort, position: reroutingTransition.currentPosition };
		}

		let targetPort = findPort(ports, transition.target, targetIndex);

		if (!targetPort) {
			return emptyLayout;
		}

		const isTargetPortMoving = isPortMovingForReroutingTransition(
			targetPort,
			'target',
			transition.id,
			reroutingTransition,
		);

		// if there are multiple links touching the same port, we only move the first one
		if (isTargetPortMoving && !didAlreadyMoveTargetPort) {
			targetPort = { ...targetPort, position: reroutingTransition.currentPosition };
			didAlreadyMoveTargetPort = true;
		}

		// Paths and labels between each source and target
		const { path, labelPosition } = manhattanPathAndLabelPosition(sourcePort, targetPort, {
			fractionOfPath: getLabelPlacement(transition.id, transitionLabelPlacements, linkIndex),
			endsPadding: 0.25,
		});

		connections.push({
			path,
			sourcePort,
			targetPort,
			labelPosition,
		});

		++linkIndex;
	}

	return {
		connections,
	};
};
